import React, { useMemo, useRef } from "react";
import ReactQuill from "react-quill";
import { apiPost } from "utils/apiFetch";
import apiPath from "utils/apiPath";
import useToastContext from "hooks/useToastContext";
import "./BlogRichEditor.css";

const FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "indent",
  "align",
  "link",
  "image",
];

function createImageHandler(quillRef, notification) {
  return function imageHandler() {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute(
      "accept",
      "image/png,image/jpeg,image/jpg,image/gif,image/webp",
    );
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const quill = quillRef.current?.getEditor?.();
      if (!quill) return;

      try {
        const formData = new FormData();
        formData.append("image", file);
        const result = await apiPost(apiPath.uploadBlogImage, formData);
        const url = result?.data?.results?.url;

        if (!result?.data?.success || !url) {
          notification.error(
            result?.data?.message || "Failed to upload image.",
          );
          return;
        }

        const range = quill.getSelection(true);
        const index = range ? range.index : quill.getLength();
        quill.insertEmbed(index, "image", url, "user");
        quill.setSelection(index + 1);
      } catch (error) {
        notification.error(error?.message || "Failed to upload image.");
      }
    };
  };
}

const BlogRichEditor = ({ value, onChange, placeholder }) => {
  const quillRef = useRef(null);
  const notification = useToastContext();

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ indent: "-1" }, { indent: "+1" }],
          [{ align: [] }],
          ["link", "image"],
          ["clean"],
        ],
        handlers: {
          image: createImageHandler(quillRef, notification),
        },
      },
    }),
    [notification],
  );

  return (
    <div className="blog-rich-editor">
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ""}
        onChange={onChange}
        modules={modules}
        formats={FORMATS}
        placeholder={placeholder}
      />
    </div>
  );
};

export default BlogRichEditor;
